/* eslint-disable consistent-return */

import { check } from 'meteor/check';
import { map, cloneDeep } from 'lodash';

import Profiles from '../Profiles';
import * as profilesOps from '../profiles-operations';
import checkAuthentication from '../../../modules/server/check-authentication';
import handleMethodException from '../../../modules/handle-method-exception';

export const editProfilesSchema = Profiles.schema.omit(
  'owner',
  'createdAt',
  'updatedAt',
  'deleted'
);
editProfilesSchema.extend({ _id: String });

/**
 * Inserts the profile in the database.
 *
 * @async
 * @param {object} profilesObject - The connector object.
 * @param {string} userId - The user id.
 * @returns {string} Return the id of the new connector.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const insert = async (profilesObject, userId) => {
  check(profilesObject, Object);
  check(userId, String);
  try {
    checkAuthentication(userId);
    const clonedProfile = cloneDeep(profilesObject);
    Profiles.schema.validate(profilesObject, {
      check,
      keys: ['name', 'geoJSON']
    });
    const newProfileId = Profiles.insert({ owner: userId, ...clonedProfile });
    return newProfileId;
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Inserts several profiles in the database.
 *
 * @param {object[]} profiles - The profiles objects.
 * @param {string} userId - The user id that owns the annotations.
 * @returns {string[]} The ids of the new annotations inserted in the database.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const insertMany = (profiles, userId) => {
  check(profiles, Array);
  check(userId, String);
  try {
    checkAuthentication();
    return map(profiles, profile => insert(profile, userId));
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Updates the profiles in the database.
 *
 * @param {object} profilesObject - The profile object.
 * @param {string} userId - The user id.
 * @returns {string} Return the id of the updated profile.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const update = (profilesObject, userId) => {
  check(profilesObject, Object);
  check(userId, String);
  try {
    checkAuthentication();
    const clonedProfile = cloneDeep(profilesObject);
    editProfilesSchema.validate(clonedProfile);
    const profileId = profilesOps.getId(clonedProfile);
    Profiles.update(profileId, { $set: clonedProfile });
    return profileId;
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Soft deletes the profiles in the database.
 *
 * @param {string} profileId - The profiles id.
 * @returns {string} Return the id of the profiles.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const softDelete = profileId => {
  check(profileId, String);
  try {
    checkAuthentication();
    return Profiles.update(profileId, { $set: { deleted: new Date().toISOString() } });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Restores the profiles in the database.
 *
 * @param {string} profileId - The profiles id.
 * @returns {string} Return the id of the profiles.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const restore = profileId => {
  check(profileId, String);
  try {
    checkAuthentication();
    return Profiles.update(profileId, { $set: { deleted: 'no' } });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Deletes the profiles from database.
 *
 * @param {string} profileId - The profiles id.
 * @returns {string} Return the id of the deleted profiles.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const hardDelete = profileId => {
  check(profileId, String);
  try {
    checkAuthentication();
    return Profiles.remove(profileId);
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Deletes many profiles from the database.
 *
 * @param {string} videoId - The annotation property for filter.
 */
export const hardManyDelete = videoId => {
  check(videoId, String);
  try {
    checkAuthentication();
    const profilesList = Profiles.find({ 'geoJSON.properties.videoId': videoId }).fetch();
    profilesList.map(item => Profiles.remove(item._id));
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Find one profile in database.
 *
 * @param {string} profileId - The annotation property for filter.
 */
export const findOne = profileId => {
  check(profileId, String);
  try {
    checkAuthentication();
    const profile = Profiles.findOne({ _id: profileId });
    return profile;
  } catch (exception) {
    handleMethodException(exception);
  }
};
